// utils/singleItemCheckout.js
import useUserStore from '../store/userStore';
import { prepareCheckoutEvent } from './analytics';

/**
 * Функция для оформления заказа отдельного товара или корзины
 * @param {Object} options - Параметры для оформления заказа
 * @param {Array} options.items - Массив товаров для оформления заказа (один элемент для одиночного товара)
 * @param {number} options.totalPrice - Общая стоимость заказа
 * @param {Function} options.setLoading - Функция для установки состояния загрузки
 * @param {Function} options.setError - Функция для установки состояния ошибки
 * @param {string} options.returnUrl - URL для возврата после оформления заказа (по умолчанию текущий URL)
 * @returns {Promise<Object>} - Объект с результатом операции
 */
export const handleCheckout = async ({
  items,
  totalPrice,
  setLoading,
  setError,
  returnUrl = window.location.href
}) => {
  console.log("[CHECKOUT] Starting checkout process");
  if (typeof setLoading === 'function') {
    setLoading(true);
  }
  
  if (typeof setError === 'function') {
    setError(null);
  }
  
  try {
    // Получаем состояние авторизации пользователя из хранилища
    const { isLoggedIn, fetchWithTokenRefresh, refreshTokenIfNeeded } = useUserStore.getState();

    // Проверяем, авторизован ли пользователь
    if (!isLoggedIn) {
      // Перенаправляем на страницу логина с параметром next
      window.location.href = `/login?next=${encodeURIComponent(returnUrl)}`;
      return { success: false, redirected: true, error: "Not logged in" };
    }

    // Используем утилиту для подготовки события checkout и сбора метаданных
    const { metadata, eventSent, ga_client_id } = await prepareCheckoutEvent(items, totalPrice);
    
    console.log("Checkout prepared:", { 
      metadataCollected: !!metadata,
      eventSent,
      clientId: ga_client_id
    });

    // Убедимся, что у нас действительный токен перед запросом
    try {
      await refreshTokenIfNeeded();
    } catch (tokenError) {
      console.error("[CHECKOUT] Failed to refresh token:", tokenError);
      if (typeof setError === 'function') {
        setError("Authentication error. Please try logging in again.");
      }
      return { success: false, error: "Authentication error" };
    }
    
    // Отправляем запрос на чекаут с использованием fetchWithTokenRefresh
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
    const response = await fetchWithTokenRefresh(
      `${backendUrl}/cart/checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          return_url: returnUrl,
          metadata: metadata  // Передаем метаданные в запросе
        }),
        requireAuth: true // Важный флаг, указывающий что нужно использовать JWT
      }
    );

    if (!response.ok) {
      console.error("[CHECKOUT] API error:", response.status);
      
      let errorMessage = "An error occurred during checkout";
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorMessage;
        console.error("[CHECKOUT] Server error details:", errorData);
      } catch (e) {
        // Если не удалось распарсить JSON с ошибкой
        console.error("[CHECKOUT] Failed to parse error response");
      }
      
      // Особая обработка ошибок аутентификации
      if (response.status === 401) {
        console.error("[CHECKOUT] Authentication failed during checkout");
        errorMessage = "Authentication error. Please try logging in again.";
        
        // Можно добавить автоматический переход на страницу логина
        setTimeout(() => {
          window.location.href = `/login?next=${encodeURIComponent(returnUrl)}`;
        }, 2000);
      }
      
      if (typeof setError === 'function') {
        setError(errorMessage);
      }
      
      return { success: false, error: errorMessage };
    }

    // Получаем URL для чекаута и перенаправляем
    const data = await response.json();
    window.location.href = data.checkout_url;
    return { success: true, checkoutUrl: data.checkout_url };
    
  } catch (error) {
    console.error('[CHECKOUT] Unexpected error:', error);
    
    if (typeof setError === 'function') {
      setError("An error occurred during checkout. Please try again.");
    }
    
    return { success: false, error: error.message || "Unknown error" };
  } finally {
    if (typeof setLoading === 'function') {
      setLoading(false);
    }
  }
};

/**
 * Функция для оформления заказа одиночного товара
 * @param {Object} options - Параметры для оформления заказа
 * @param {Object} options.item - Товар для оформления заказа
 * @param {number} options.price - Стоимость товара
 * @param {string} options.slug - Идентификатор товара
 * @param {string} options.collection - Коллекция товара
 * @param {string} options.mediaType - Тип медиа (icon, iconset, etc.)
 * @param {Function} options.setLoading - Функция для установки состояния загрузки
 * @param {Function} options.setError - Функция для установки состояния ошибки
 * @returns {Promise<Object>} - Объект с результатом операции
 */
export const singleItemCheckout = async ({
  item,
  price,
  slug,
  collection,
  mediaType,
  setLoading,
  setError
}) => {
  // Если передан готовый объект товара, используем его, иначе создаем новый
  const checkoutItem = item || {
    assetId: `${collection}-${slug}`,
    slug,
    collection,
    mediaType: mediaType || 'icon',
    product: {
      price: price
    }
  };
  
  // Создаем массив из одного товара
  const items = [checkoutItem];
  
  // Общая стоимость заказа (для одного товара это просто цена)
  const totalPrice = price;
  
  // Вызываем общую функцию оформления заказа
  return handleCheckout({
    items,
    totalPrice,
    setLoading,
    setError
  });
};

export default singleItemCheckout;